import { Handle, Position, useUpdateNodeInternals, type Node, type NodeProps } from '@xyflow/react';
import { memo, useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { cn, InlineText } from '@/core/ui';
import {
  ArrowDown,
  ArrowUp,
  Copy,
  DotsThreeVertical,
  Flag,
  Play,
  Trash,
  Warning,
  WarningCircle,
} from '@/core/ui/icons';
import {
  blockPorts,
  blockSpec,
  blockSummary,
  isBlockType,
  portHandleId,
  type Port,
} from '../../domain/blocks';
import { TARGET_HANDLE, type CardNodeData } from '../../domain/canvas';
import {
  afterTerminal,
  deleteBlock,
  deleteGroup,
  duplicateGroup,
  moveBlock,
  setStart,
  updateGroup,
} from '../../domain/document';
import type { IssueLevel } from '../../domain/issues';
import type { Block, DocGroup } from '../../domain/types';
import { BlockGlyph } from '../BlockGlyph';
import { CATEGORY_TONE } from '../icons';
import { Menu, MenuItem } from '../Menu';
import { DRAG_MIME, selectedBlockId, useBuilder } from './context';

export type CardFlowNode = Node<CardNodeData, 'card'>;

const RING: Record<IssueLevel, string> = {
  error: 'ring-2 ring-rose-300',
  warning: 'ring-2 ring-amber-300',
};

const hasDrag = (e: DragEvent) => e.dataTransfer.types.includes(DRAG_MIME);

/**
 * Um card do canvas: título, blocos de cima para baixo e as saídas — uma alça
 * por opção/condição, a de erro das integrações e a padrão no último bloco.
 * Aceita blocos arrastados da paleta (soltos sobre um bloco entram acima dele).
 */
export const CardNode = memo(function CardNode({ id, data, selected }: NodeProps<CardFlowNode>) {
  const { group, isStart } = data;
  const { doc, issues, selection, select, update, addBlock, testFrom } = useBuilder();
  const [dropping, setDropping] = useState(false);
  const updateInternals = useUpdateNodeInternals();
  const level = issues.groups.get(id);
  const activeBlock = selectedBlockId(selection);
  const dead = afterTerminal(group);

  // As alças mudam de lugar quando os blocos mudam (ordem, opções): o canvas remede.
  const signature = group.blocks
    .map(
      (b, i) =>
        `${b.id}[${blockPorts(b, i === group.blocks.length - 1)
          .map((p) => p.itemId)
          .join(',')}]`,
    )
    .join('|');
  // Só quando muda depois de montar: na montagem o canvas já mede (e chamar aqui
  // anteciparia o fitView inicial com um card só medido).
  const measuredFor = useRef(signature);
  useEffect(() => {
    if (measuredFor.current === signature) return;
    measuredFor.current = signature;
    updateInternals(id);
  }, [id, signature, updateInternals]);

  // Ligações de portas que não existem mais (ex.: bloco deixou de ser o último) continuam
  // visíveis, em vermelho, para a pessoa refazer ou apagar.
  const stale = (block: Block, ports: Port[]) =>
    doc.edges
      .filter((e) => e.from_block_id === block.id && !ports.some((p) => p.itemId === e.from_item_id))
      .map((e): Port => ({
        blockId: block.id,
        itemId: e.from_item_id,
        kind: 'next',
        label: 'Saída inválida',
      }));

  const onDrop = (e: DragEvent, index?: number) => {
    if (!hasDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setDropping(false);
    const type = e.dataTransfer.getData(DRAG_MIME);
    if (isBlockType(type)) addBlock(id, type, index);
  };

  return (
    <div
      onDragOver={(e) => {
        if (!hasDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setDropping(true);
      }}
      onDragLeave={() => setDropping(false)}
      onDrop={(e) => onDrop(e)}
      className={cn(
        'w-72 rounded-3xl bg-surface p-2 shadow-sm transition-shadow',
        level && RING[level],
        selected && !activeBlock && 'ring-2 ring-brand-400',
        dropping && 'ring-2 ring-brand-300 ring-offset-2',
      )}
    >
      <Handle type="target" id={TARGET_HANDLE} position={Position.Left} className="flow-handle-in" />

      <header className="flex items-center gap-1.5 px-2 pt-1 pb-2">
        {isStart && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-nav-active px-2 py-0.5 text-[11px] font-semibold text-nav-active-fg">
            <Flag aria-hidden weight="fill" className="size-3" />
            Início
          </span>
        )}
        <div className="nodrag min-w-0 flex-1 text-sm font-semibold text-fg">
          <InlineText
            value={group.title}
            label="título do card"
            placeholder="Card sem título"
            maxLength={300}
            onSave={async (title) => update((d) => updateGroup(d, id, { title }))}
          />
        </div>
        {level && <LevelIcon level={level} />}
        <div className="nodrag">
          <Menu
            label={`Ações do card ${group.title}`}
            trigger={<DotsThreeVertical aria-hidden className="size-4" />}
            triggerClassName="rounded-lg p-1 text-fg-subtle hover:bg-surface-2 hover:text-fg"
          >
            {(close) => (
              <>
                <MenuItem icon={Play} onSelect={() => (close(), testFrom(id))}>
                  Testar a partir daqui
                </MenuItem>
                <MenuItem
                  icon={Flag}
                  disabled={isStart}
                  onSelect={() => (close(), update((d) => setStart(d, id)))}
                >
                  Definir como início
                </MenuItem>
                <MenuItem
                  icon={Copy}
                  onSelect={() => {
                    close();
                    const copy = duplicateGroup(doc, id);
                    update(() => copy.doc);
                    select({ groupId: copy.groupId });
                  }}
                >
                  Duplicar
                </MenuItem>
                <MenuItem
                  icon={Trash}
                  onSelect={() => {
                    close();
                    update((d) => deleteGroup(d, id));
                    select(null);
                  }}
                >
                  Excluir card
                </MenuItem>
              </>
            )}
          </Menu>
        </div>
      </header>

      {group.blocks.length === 0 ? (
        <p className="rounded-2xl bg-surface-2 px-3 py-4 text-center text-xs text-fg-subtle">
          Arraste um bloco da paleta para cá
          <br />
          ou selecione o card e clique num bloco.
        </p>
      ) : (
        <ol className="space-y-1.5">
          {group.blocks.map((block, index) => {
            const ports = blockPorts(block, index === group.blocks.length - 1);
            return (
              <BlockRow
                key={block.id}
                block={block}
                group={group}
                index={index}
                ports={[...ports, ...stale(block, ports)]}
                level={issues.blocks.get(block.id)}
                active={activeBlock === block.id}
                unreachable={dead.has(block.id)}
                onSelect={() => select({ groupId: id, blockId: block.id })}
                onMove={(dir) => update((d) => moveBlock(d, block.id, dir))}
                onDelete={() => {
                  update((d) => deleteBlock(d, block.id));
                  select({ groupId: id });
                }}
                onDrop={(e) => onDrop(e, index)}
              />
            );
          })}
        </ol>
      )}
    </div>
  );
});

function LevelIcon({ level }: { level: IssueLevel }) {
  return level === 'error' ? (
    <WarningCircle aria-label="Com erro" weight="fill" className="size-4 shrink-0 text-rose-500" />
  ) : (
    <Warning aria-label="Com aviso" weight="fill" className="size-4 shrink-0 text-amber-500" />
  );
}

interface RowProps {
  block: Block;
  group: DocGroup;
  index: number;
  ports: Port[];
  level?: IssueLevel;
  active: boolean;
  unreachable: boolean;
  onSelect: () => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
  onDrop: (e: DragEvent) => void;
}

function BlockRow({
  block,
  group,
  index,
  ports,
  level,
  active,
  unreachable,
  onSelect,
  onMove,
  onDelete,
  onDrop,
}: RowProps) {
  const { doc } = useBuilder();
  const spec = blockSpec(block.type);
  // A saída simples (sem rótulo) fica na própria linha; as nomeadas, uma por linha.
  const inline = ports.length === 1 && ports[0]!.kind === 'next' && !ports[0]!.label ? ports[0] : null;
  const listed = inline ? [] : ports;
  const last = index === group.blocks.length - 1;

  return (
    <li
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDrop={onDrop}
      className={cn(
        'group/row relative cursor-pointer rounded-2xl bg-surface-2 px-2.5 py-2 transition-colors hover:bg-gray-100',
        level && RING[level],
        active && 'bg-nav-active ring-2 ring-brand-400',
        unreachable && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-2">
        <span
          className={cn(
            'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-lg',
            CATEGORY_TONE[spec.category],
          )}
        >
          <BlockGlyph type={block.type} className="size-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold tracking-wide text-fg-subtle uppercase">{spec.label}</p>
          <p className="line-clamp-2 text-xs break-words text-fg">{blockSummary(block, doc.groups)}</p>
        </div>
        {level && <LevelIcon level={level} />}
      </div>

      <div className="nodrag absolute top-1 right-1 hidden gap-0.5 rounded-lg bg-surface p-0.5 shadow-sm group-hover/row:flex group-focus-within/row:flex">
        <RowButton label="Subir bloco" disabled={index === 0} onClick={() => onMove(-1)}>
          <ArrowUp aria-hidden className="size-3" />
        </RowButton>
        <RowButton label="Descer bloco" disabled={last} onClick={() => onMove(1)}>
          <ArrowDown aria-hidden className="size-3" />
        </RowButton>
        <RowButton label="Apagar bloco" onClick={onDelete}>
          <Trash aria-hidden className="size-3" />
        </RowButton>
      </div>

      {listed.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {listed.map((p) => (
            <li
              key={portHandleId(p.blockId, p.itemId)}
              className={cn(
                'relative rounded-lg bg-surface px-2 py-1 pr-4 text-[11px]',
                p.label === 'Saída inválida' ? 'text-rose-600' : 'text-fg-muted',
                p.kind === 'error' && 'text-rose-600',
              )}
            >
              <span className="line-clamp-1">{p.label || 'Próximo card'}</span>
              <Handle
                type="source"
                position={Position.Right}
                id={portHandleId(p.blockId, p.itemId)}
                style={{ right: -18 }}
                className={cn(
                  'flow-handle-out',
                  (p.kind === 'error' || p.label === 'Saída inválida') && 'flow-handle-error',
                )}
              />
            </li>
          ))}
        </ul>
      )}
      {inline && (
        <Handle
          type="source"
          position={Position.Right}
          id={portHandleId(inline.blockId, null)}
          style={{ right: -8 }}
          className="flow-handle-out"
        />
      )}
    </li>
  );
}

function RowButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="rounded-md p-1 text-fg-subtle hover:bg-surface-2 hover:text-fg disabled:opacity-40"
    >
      {children}
    </button>
  );
}
